import React, {
	useCallback,
	type KeyboardEvent,
	type MouseEvent,
	forwardRef,
	type ForwardedRef,
	type RefObject,
} from 'react';
import { useIntl } from 'react-intl-next';
import { mergeRefs } from 'use-callback-ref';
import { IconButton } from '@atlaskit/button/new';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { HeadingItem, MenuGroup, Section } from '@atlaskit/menu';
import Popup, { type TriggerProps, type ContentProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { DEFAULT_MENU_MAX_HEIGHT, listItemMenu } from '../../../../../../common/constants';
import { useListItemMenuOptions } from '../../../../../../common/context';
import { useCreateTrigger } from '../../../../../../common/context/side-effect-marshal/focus-marshal/use-create-trigger';
import type {
	ItemId,
	OnCreateClick,
	ListItemOption,
	ListItemSection,
	ItemDropPayload,
} from '../../../../../../common/types';
import type { SetCloseMenuCallback } from '../../../../../../common/types/item';
import type { RenderListItemMenuOption } from '../../../../../../renderers';
import { ListItemMenuOption } from '../../list-item-menu-option';
import { renderOverrideLabel } from '../utils';
import ItemSectionOptions from './item-section-options';
import messages from './messages';

type Props = {
	id: ItemId;
	isOpen: boolean;
	issuesSectionOptions: ListItemOption[];
	customOtherSections: ListItemSection[];
	parentId: ItemId | undefined;
	level: number;
	depth: number;
	isCreateChildEnabled: boolean;
	isCreateSiblingEnabled: boolean;
	menuRef: RefObject<HTMLDivElement>;
	onInlineCreateClicked: OnCreateClick;
	renderListItemMenuOption: RenderListItemMenuOption;
	onDrop: (id: ItemId, payload: ItemDropPayload) => void;
	onMenuToggle: (e: MouseEvent | KeyboardEvent) => void;
	setCloseMenu: SetCloseMenuCallback;
	setOpenModal: (event: MouseEvent | KeyboardEvent, optionId: string) => void;
};

const MeatballPopup = forwardRef(
	(
		{
			id,
			isOpen,
			issuesSectionOptions,
			customOtherSections,
			parentId,
			level,
			depth,
			onDrop,
			menuRef,
			isCreateChildEnabled,
			isCreateSiblingEnabled,
			onInlineCreateClicked,
			renderListItemMenuOption,
			onMenuToggle,
			setCloseMenu,
			setOpenModal,
		}: Props,
		ref: ForwardedRef<HTMLButtonElement>,
	) => {
		const { createTriggerRef, onCreateTriggerClick } = useCreateTrigger(id, 'MEATBALLS');
		const [{ overrideLabels }] = useListItemMenuOptions();
		const { formatMessage } = useIntl();

		const renderMenu = useCallback(
			({ setInitialFocusRef }: ContentProps) => (
				<Box ref={menuRef} xcss={menuWrapStyles}>
					<MenuGroup
						testId="roadmap.timeline-table.main.content.list.list-item.meatballs-button.meatball-popup.menu"
						maxHeight={DEFAULT_MENU_MAX_HEIGHT}
					>
						{issuesSectionOptions.length > 0 && (
							<Section testId="roadmap.timeline-table.main.content.list.list-item.meatballs-button.meatball-popup.items-section">
								<HeadingItem>
									{renderOverrideLabel(formatMessage, overrideLabels[listItemMenu.ITEMS_SECTION]) ||
										formatMessage(messages.itemsSectionHeader)}
								</HeadingItem>
								<ItemSectionOptions
									issuesSectionOptions={issuesSectionOptions}
									id={id}
									parentId={parentId}
									level={level}
									depth={depth}
									isCreateChildEnabled={isCreateChildEnabled}
									isCreateSiblingEnabled={isCreateSiblingEnabled}
									overrideLabels={overrideLabels}
									onInlineCreateClicked={onInlineCreateClicked}
									setInitialFocusRef={setInitialFocusRef}
									onDrop={onDrop}
									renderListItemMenuOption={renderListItemMenuOption}
									onCreateTriggerClick={onCreateTriggerClick}
									setCloseMenu={setCloseMenu}
									setOpenModal={setOpenModal}
								/>
							</Section>
						)}
						{customOtherSections.map(({ id: sectionId, title, options }) => (
							<Section key={sectionId} hasSeparator>
								<HeadingItem>{renderOverrideLabel(formatMessage, title)}</HeadingItem>

								{options.map(({ id: optionId }, index) => (
									<div key={optionId}>
										{renderListItemMenuOption({
											id,
											optionId,
											setInitialFocusRef:
												issuesSectionOptions.length === 0 && index === 0
													? setInitialFocusRef
													: undefined,
											setOpenModal: (event) => {
												setOpenModal(event, optionId);
											},
											Option: ListItemMenuOption,
											onCreateTriggerClick,
											setCloseMenu,
										})}
									</div>
								))}
							</Section>
						))}
					</MenuGroup>
				</Box>
			),
			[
				customOtherSections,
				depth,
				formatMessage,
				id,
				isCreateChildEnabled,
				isCreateSiblingEnabled,
				issuesSectionOptions,
				level,
				menuRef,
				onCreateTriggerClick,
				onDrop,
				onInlineCreateClicked,
				overrideLabels,
				parentId,
				renderListItemMenuOption,
				setCloseMenu,
				setOpenModal,
			],
		);

		const trigger = useCallback(
			({ ref: triggerRef, ...triggerProps }: TriggerProps) => (
				<Box xcss={buttonWrapStyles}>
					<IconButton
						{...triggerProps}
						ref={mergeRefs([ref, createTriggerRef, triggerRef])}
						onClick={onMenuToggle}
						spacing="compact"
						testId={`roadmap.timeline-table.main.content.list.list-item.more-options-button.meatballs-menu-${id}`}
						label={formatMessage(messages.meatballsButtonLabel)}
						icon={MoreIcon}
					/>
				</Box>
			),
			[createTriggerRef, formatMessage, id, onMenuToggle, ref],
		);

		const onPopupClose = useCallback(
			(event: MouseEvent | KeyboardEvent) => setCloseMenu(event),
			[setCloseMenu],
		);

		return (
			<Popup
				role="dialog"
				shouldFlip={false}
				shouldRenderToParent
				shouldReturnFocus={false}
				shouldUseCaptureOnOutsideClick
				onClose={onPopupClose}
				placement="right-start"
				isOpen={isOpen}
				content={renderMenu}
				trigger={trigger}
			/>
		);
	},
);

const buttonWrapStyles = xcss({
	marginLeft: 'space.050',
});

const menuWrapStyles = xcss({
	cursor: 'default',
});

export default MeatballPopup;
